import React, { useState, useEffect } from 'react';
import { referenceService } from '../services';
import { Asset, AssetName, AttributeSensitivity, formatDatasetPath } from '../helpers';
import { DataTable, Tabs } from '../components';
import { useQueryParams, useMessage, usePrincipal } from '../hooks';

const objectSortOrder = [
  'attribute_value',
  'attribute',
  'entity',
  'glossary',
  'dataset',
  'dataset_group',
  'datasource',
  'system',
];

const getDefaultObject = toReferencesCount => {
  let defaultObject;

  toReferencesCount &&
    Object.entries(toReferencesCount).forEach(object => {
      const objectName = object[0];
      const objectCount = object[1];

      if (objectCount > 0 && defaultObject === undefined && objectName !== 'total') {
        defaultObject = objectName;
      }
    });

  return defaultObject;
};

export const fetchToReferencesCount = async ({ toObjectName, toObjectId, abortFetch }) => {
  return referenceService
    .getToReferencesCount(toObjectName, toObjectId, abortFetch)
    .then(res => {
      let tmp = { total: 0 };

      if (res?.to_references_count) {
        res.to_references_count.sort(
          (a, b) =>
            objectSortOrder.indexOf(a.from_object_name) -
            objectSortOrder.indexOf(b.from_object_name)
        );

        res.to_references_count.forEach(result => {
          const object_count = parseInt(result.to_references_count);
          tmp = {
            ...tmp,
            [result.from_object_name]: object_count,
            total: tmp.total + object_count,
          };
        });
      }

      return tmp;
    })
    .catch(err => {
      throw err;
    });
};

export const fetchToReferencesCountPerChild = async ({
  toObjectName,
  toParentObjectName,
  toParentObjectId,
  abortFetch,
}) => {
  return referenceService
    .getToReferencesCountPerChild(toObjectName, toParentObjectName, toParentObjectId, abortFetch)
    .then(res => {
      return res.to_references_count || [];
    })
    .catch(err => {
      throw err;
    });
};

export const References = ({ toObjectName, toObjectId, toReferencesCount }) => {
  const { queryParams, setHistory } = useQueryParams();

  const [abortFetch, setAbortFetch] = useState(new AbortController());
  const [toReferences, setToReferences] = useState([]);
  const [loadingReferences, setLoadingReferences] = useState(true);

  const [defaultSelectedObject, setDefaultSelectedObject] = useState({
    tab: queryParams.object || getDefaultObject(toReferencesCount),
  });

  const { showMessage } = useMessage();
  const { fetchPrincipals } = usePrincipal();

  useEffect(() => {
    if (defaultSelectedObject.tab) {
      fetchToReferences(defaultSelectedObject.tab);
    } else {
      setLoadingReferences(false); // Needed to show "no result" message
    }
  }, [defaultSelectedObject]);

  useEffect(() => {
    setDefaultSelectedObject({ tab: queryParams.object || getDefaultObject(toReferencesCount) });
  }, [toReferencesCount]);

  useEffect(() => {
    // Cancel fetches
    return () => {
      abortFetch.abort();
    };
  }, []);

  const fetchToReferences = async objectName => {
    if (objectName) {
      // objectName is undefined if dataset does not have any references
      referenceService
        .getByToId(toObjectName, toObjectId, objectName, abortFetch)
        .then(res => {
          if (res) {
            let result = res.references;

            if (objectName === Asset.Attribute) {
              result = result.map(x => ({
                ...x,
                pii: x.is_pii ? 'PII' : null,
                pii_tooltip: x.is_pii
                  ? 'Classified as Personally Identifiable Information (PII)'
                  : null,
              }));
            }

            if (objectName === Asset.Dataset) {
              // Render each referenced dataset as its full endpoint path (API datasets); the
              // ancestors are attached to the row by the references endpoint.
              result = result.map(x => ({
                ...x,
                dataset_path: formatDatasetPath(x, { ancestors: x.ancestors }),
              }));
            }

            setToReferences(result);

            setLoadingReferences(false);

            fetchPrincipals(result, setToReferences);
          }
        })
        .catch(err => {
          showMessage(err, 'error');
        });
    } else {
      setLoadingReferences(false); // Needed to show "no result" message
    }
  };

  const onObjectChange = object => {
    setHistory({ object: object });
    fetchToReferences(object);
  };

  return (
    <React.Fragment>
      {toReferencesCount && toReferencesCount.total > 0 ? (
        <Tabs
          className="buttons left"
          onTabChange={onObjectChange}
          showResultCount={false}
          disableTabsWithoutResults={true}
          defaultSelectedTab={defaultSelectedObject}
        >
          <div
            label="Attribute Values"
            tabid={Asset.AttributeValue}
            resultCount={(toReferencesCount && toReferencesCount.attribute_value) || 0}
          >
            <DataTable
              columns={[
                {
                  id: 'attribute_value_name',
                  name: 'Name',
                  link: '/attributes/:attribute_id/:attribute_value_id',
                  tooltip: 'attribute_value_description',
                  className: 'bold',
                },
                { id: 'attribute_value_code', name: 'Code' },
                {
                  id: 'attribute_value_description',
                  name: 'Description',
                  type: 'description',
                  viewMode: 'descriptions',
                },
                {
                  id: 'attribute_name',
                  name: 'Attribute',
                  link: '/attributes/:attribute_id',
                  viewMode: 'descriptions',
                  // inline: [{ type: 'icon', icon: 'attribute_role_name' }],
                },
              ]}
              data={toReferences}
              idColumn="attribute_value_id"
              loading={loadingReferences}
              editable={false}
              filterable={true}
              filterObjects={[
                'attribute_value_name',
                'attribute_value_code',
                'attribute_value_description',
                'attribute_name',
              ]}
              showViewModes={true}
            />
          </div>
          <div
            label="Attributes"
            tabid={Asset.Attribute}
            resultCount={(toReferencesCount && toReferencesCount.attribute) || 0}
          >
            <DataTable
              columns={[
                {
                  id: 'attribute_role_name',
                  name: '',
                  type: 'icon',
                  tooltip: 'attribute_role_name',
                },
                {
                  id: 'attribute_name',
                  name: 'Name',
                  link: '/attributes/:attribute_id',
                  tooltip: 'attribute_description',
                  className: 'bold',
                },
                {
                  id: 'attribute_unit_code',
                  name: 'Attribute Unit',
                  tooltip: 'attribute_unit_name',
                },
                {
                  id: 'attribute_description',
                  name: 'Description',
                  type: 'description',
                  viewMode: 'descriptions',
                },
                {
                  // An attribute belongs to a glossary, and the same name can be used in another one.
                  id: 'glossary_name',
                  name: 'Glossary',
                  link: '/glossaries/:glossary_id',
                },
                {
                  id: 'pii',
                  name: '',
                  type: 'icon',
                  tooltip: 'pii_tooltip',
                  className: 'no-right-margin',
                },
                {
                  id: 'attribute_sensitivity_name',
                  name: 'Data Sensitivity',
                  type: 'pill',
                  value: 'attribute_sensitivity_level',
                  values: AttributeSensitivity,
                  tooltip: 'attribute_sensitivity_name',
                },
                {
                  id: 'principal_name',
                  name: 'Owner',
                  type: 'owner',
                  link: '/:principal_url',
                  data: { photo: 'principal_photo' },
                  tooltipSubtitle: 'principal_type_name',
                },
              ]}
              data={toReferences}
              idColumn="attribute_id"
              loading={loadingReferences}
              editable={false}
              filterable={true}
              filterObjects={[
                'attribute_name',
                'attribute_description',
                'attribute_role_name',
                'attribute_unit_name',
                'glossary_name',
                'principal_name',
              ]}
              showViewModes={true}
            />
          </div>

          <div
            label="Entities"
            tabid={Asset.Entity}
            resultCount={(toReferencesCount && toReferencesCount.entity) || 0}
          >
            <DataTable
              columns={[
                {
                  id: 'entity_name',
                  name: 'Name',
                  link: '/glossaries/:glossary_id/:entity_id',
                  tooltip: 'entity_description',
                  className: 'bold',
                },
                {
                  id: 'entity_description',
                  name: 'Description',
                  type: 'description',
                  viewMode: 'descriptions',
                },
                { id: 'glossary_name', name: 'Glossary', link: '/glossaries/:glossary_id' },
                {
                  id: 'principal_name',
                  name: 'Owner',
                  type: 'owner',
                  link: '/:principal_url',
                  data: { photo: 'principal_photo' },
                  tooltipSubtitle: 'principal_type_name',
                },
              ]}
              data={toReferences}
              idColumn="entity_id"
              loading={loadingReferences}
              editable={false}
              filterable={true}
              filterObjects={[
                'entity_name',
                'entity_description',
                'glossary_name',
                'principal_name',
              ]}
              showViewModes={true}
            />
          </div>

          <div
            label="Glossaries"
            tabid={Asset.Glossary}
            resultCount={(toReferencesCount && toReferencesCount.glossary) || 0}
          >
            <DataTable
              columns={[
                {
                  id: 'glossary_name',
                  name: 'Name',
                  link: '/glossaries/:glossary_id',
                  tooltip: 'glossary_description',
                  className: 'bold',
                },
                { id: 'glossary_type', name: 'Type', className: 'small-text' },
                {
                  id: 'glossary_description',
                  name: 'Description',
                  type: 'description',
                  viewMode: 'descriptions',
                },
                {
                  id: 'principal_name',
                  name: 'Owner',
                  type: 'owner',
                  link: '/:principal_url',
                  data: { photo: 'principal_photo' },
                  tooltipSubtitle: 'principal_type_name',
                },
              ]}
              data={toReferences}
              idColumn="glossary_id"
              loading={loadingReferences}
              editable={false}
              filterable={true}
              filterObjects={[
                'glossary_name',
                'glossary_type',
                'glossary_description',
                'principal_name',
              ]}
              showViewModes={true}
            />
          </div>

          <div
            label="Datasets"
            tabid={Asset.Dataset}
            resultCount={(toReferencesCount && toReferencesCount.dataset) || 0}
          >
            <DataTable
              columns={[
                { id: 'dataset_type_name', name: '', type: 'icon', tooltip: 'dataset_type_name' },
                {
                  // dataset_path holds the formatDatasetPath() output (its segments link
                  // themselves); sort still keys off dataset_name.
                  id: 'dataset_path',
                  name: 'Name',
                  sortField: 'dataset_name',
                  className: 'bold',
                },
                {
                  id: 'dataset_description',
                  name: 'Description',
                  type: 'description',
                  viewMode: 'descriptions',
                },
                {
                  id: 'dataset_group_name',
                  name: 'Dataset Group',
                  link: '/systems/:system_id/:datasource_id/:dataset_group_id',
                },
                {
                  id: 'datasource_name',
                  name: 'Datasource',
                  link: '/systems/:system_id/:datasource_id',
                  viewMode: 'statistics',
                },
                {
                  id: 'system_name',
                  name: 'System',
                  link: '/systems/:system_id',
                  viewMode: 'statistics',
                },
                {
                  id: 'principal_name',
                  name: 'Owner',
                  type: 'owner',
                  link: '/:principal_url',
                  data: { photo: 'principal_photo' },
                  tooltipSubtitle: 'principal_type_name',
                },
              ]}
              data={toReferences}
              idColumn="dataset_id"
              loading={loadingReferences}
              editable={false}
              filterable={true}
              filterObjects={[
                'dataset_name',
                'dataset_description',
                'dataset_type_name',
                'dataset_group_name',
                'datasource_name',
                'system_name',
                'principal_name',
              ]}
              showViewModes={true}
            />
          </div>

          <div
            label="Dataset Groups"
            tabid={Asset.DatasetGroup}
            resultCount={(toReferencesCount && toReferencesCount.dataset_group) || 0}
          >
            <DataTable
              columns={[
                {
                  id: 'dataset_group_name',
                  name: 'Name',
                  link: '/systems/:system_id/:datasource_id/:dataset_group_id',
                  tooltip: 'dataset_group_description',
                  className: 'bold',
                },
                {
                  id: 'dataset_group_description',
                  name: 'Description',
                  type: 'description',
                  viewMode: 'descriptions',
                },
                {
                  id: 'datasource_name',
                  name: 'Datasource',
                  link: '/systems/:system_id/:datasource_id',
                },
                {
                  id: 'system_name',
                  name: 'System',
                  link: '/systems/:system_id',
                  viewMode: 'statistics',
                },
                {
                  id: 'principal_name',
                  name: 'Owner',
                  type: 'owner',
                  link: '/:principal_url',
                  data: { photo: 'principal_photo' },
                  tooltipSubtitle: 'principal_type_name',
                },
              ]}
              data={toReferences}
              idColumn="dataset_group_id"
              loading={loadingReferences}
              editable={false}
              filterable={true}
              filterObjects={[
                'dataset_group_name',
                'dataset_group_description',
                'datasource_name',
                'system_name',
                'principal_name',
              ]}
              showViewModes={true}
            />
          </div>

          <div
            label="Datasources"
            tabid={Asset.Datasource}
            resultCount={(toReferencesCount && toReferencesCount.datasource) || 0}
          >
            <DataTable
              columns={[
                {
                  id: 'datasource_type_category',
                  name: '',
                  type: 'icon',
                  tooltip: 'datasource_type_name',
                },
                {
                  id: 'datasource_name',
                  name: 'Name',
                  link: '/systems/:system_id/:datasource_id',
                  tooltip: 'datasource_description',
                  className: 'bold',
                },
                {
                  id: 'datasource_description',
                  name: 'Description',
                  type: 'description',
                  viewMode: 'descriptions',
                },
                { id: 'system_name', name: 'System', link: '/systems/:system_id' },
                {
                  id: 'principal_name',
                  name: 'Owner',
                  type: 'owner',
                  link: '/:principal_url',
                  data: { photo: 'principal_photo' },
                  tooltipSubtitle: 'principal_type_name',
                },
              ]}
              data={toReferences}
              idColumn="datasource_id"
              loading={loadingReferences}
              editable={false}
              filterable={true}
              filterObjects={[
                'datasource_name',
                'datasource_description',
                'datasource_type_category',
                'datasource_type_name',
                'system_name',
                'principal_name',
              ]}
              showViewModes={true}
            />
          </div>

          <div
            label="Systems"
            tabid={Asset.System}
            resultCount={(toReferencesCount && toReferencesCount.system) || 0}
          >
            <DataTable
              columns={[
                {
                  id: 'system_name',
                  name: 'Name',
                  link: '/systems/:system_id',
                  tooltip: 'system_description',
                  className: 'bold',
                },
                { id: 'system_type', name: 'Type', className: 'small-text' },
                {
                  id: 'system_description',
                  name: 'Description',
                  type: 'description',
                  viewMode: 'descriptions',
                },
                {
                  id: 'principal_name',
                  name: 'Owner',
                  type: 'owner',
                  link: '/:principal_url',
                  data: { photo: 'principal_photo' },
                  tooltipSubtitle: 'principal_type_name',
                },
              ]}
              data={toReferences}
              idColumn="system_id"
              loading={loadingReferences}
              editable={false}
              filterable={true}
              filterObjects={['system_name', 'system_type', 'system_description', 'principal_name']}
              showViewModes={true}
            />
          </div>
        </Tabs>
      ) : (
        !loadingReferences && (
          <div className="no-result-text">{`No references to this ${AssetName[toObjectName]}`}</div>
        )
      )}
    </React.Fragment>
  );
};
